import { Box, Button, Dialog, IconButton } from "@mui/material";
import { ChangeEvent, Dispatch, DragEvent, FC, SetStateAction, useId, useState } from "react";
import { FileUpload, UploaderArgs, UploaderConfig } from "./multipart-upload.types";
import FileUploadProgress from "./FileUploadProgress";
import FileUploadPrintout from "./FileUploadPrintout";
import { Close, Upload } from "@mui/icons-material";
import produce from "immer";
import { Uploader } from "./Uploader";
import { P } from "theme/typography";
import theme from "theme";
import { StageNumber } from "shared/StageNumber";

const UploadDialog: FC<{
  open: boolean;
  close: () => void;
  caseId: number;
  claimantName: string;
  UploaderClazz: new (args: UploaderArgs) => Uploader;
  onUnAuthorized: () => void;
  setUploads: Dispatch<SetStateAction<Uploader[]>>;
}> = ({ open, close, caseId, claimantName, UploaderClazz, onUnAuthorized, setUploads }) => {
  const [isDraggedOn, setIsDraggedOn] = useState<boolean>(false);
  const batchId = useId();
  const [files, setFiles] = useState<FileUpload[]>([]);
  const anyFilesUploading = files.some((f) => f.state === "inprogress" || f.state === "queued");
  const allFilesUploaded =
    Boolean(files.length) && files.every((f) => f.state !== "idle" && (f.state === "error" || f.state === "success"));
  const filesForUpload = files.filter((f) => !f.error && !f.clientError);
  const successFiles = files.filter((f) => f.state === "success");
  const errorFiles = files.filter((f) => f.state === "error" && !f.clientError);

  const batchComplete = allFilesUploaded;
  const batchHadErrors = allFilesUploaded && errorFiles.length > 0;

  const invalidClientErrorFiles = files.filter((f) => f.clientError);
  const isClientErrors = invalidClientErrorFiles.length > 0;
  const anyFilesUploadingOrHaveUploaded: boolean =
    anyFilesUploading || Boolean(successFiles.length) || Boolean(errorFiles.length);

  const handleFiles = (fileList: File[]) => {
    const newUploads: FileUpload[] = fileList
      .filter((f) => !files.find((file) => file.file.name === f.name))
      .map((f) => {
        const baseFile = {
          file: f,
          batchId,
          progress: 0,
          bytesSent: 0,
        };
        if (f.size > UploaderConfig.maxUploadSizeBytes) {
          return {
            ...baseFile,
            state: "error",
            clientError: "size_exceeded",
          };
        } else if (!UploaderConfig.checkUploadType(f.type)) {
          return {
            ...baseFile,
            state: "error",
            clientError: "invalid_type",
          };
        } else if (f.size === 0) {
          return {
            ...baseFile,
            state: "error",
            clientError: "empty_document",
          };
        } else {
          return {
            ...baseFile,
            state: "idle",
          };
        }
      });
    setFiles((prev) => [...prev, ...newUploads]);
  };

  const onFiles = (evt: ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(evt.target.files || []));
  };

  const beginUploads = () => {
    const validFiles = files.filter((f) => f.state === "idle" && !f.error && !f.clientError);
    const uploaders = validFiles.map((file, idx) => {
      setFiles((prev) =>
        produce(prev, (draft) => {
          const val = draft[idx];

          if (val !== undefined) {
            val.state = "queued";
          }
        })
      );
      const uploader = new UploaderClazz({
        file: file.file,
        caseId,
        batchId,
        preserveSectionStateForSections: [],
        onComplete: () => {
          setFiles((prev) =>
            produce(prev, (draft) => {
              const val = draft[idx];

              if (val !== undefined) {
                val.state = "success";
              }
            })
          );
        },
        onProgress: (_partIndex, _partProgress, _partTotal, totalSent) => {
          setFiles((prev) =>
            produce(prev, (draft) => {
              const val = draft[idx];

              if (val !== undefined) {
                val.progress = (totalSent / file.file.size) * 100;
                if (val.state === "idle" || val.state === "queued") {
                  val.state = "inprogress";
                }
              }
            })
          );
        },
        onFailure: (reason: Uploader["failureReason"], message?: string) => {
          if (reason === "unauthorized") {
            onUnAuthorized();
          }
          setFiles((prev) =>
            produce(prev, (draft) => {
              const val = draft[idx];

              if (val !== undefined) {
                val.error = (reason !== "unknown" && reason) || message || "Error";
                val.state = "error";
              }
            })
          );
        },
      });
      uploader.sendBeginUpload();
      return uploader;
    });
    setUploads((prev) => [...prev, ...uploaders]);
  };

  const onFileDelete = (file: FileUpload) => {
    setFiles((currFiles) => {
      const foundIndex = currFiles.findIndex((cf) => cf === file);
      if (foundIndex > -1) {
        const copy = currFiles.slice();
        copy.splice(foundIndex, 1);
        return copy;
      } else {
        return currFiles;
      }
    });
  };

  return (
    <Dialog open={open} onClose={anyFilesUploading ? undefined : close} fullWidth maxWidth="md">
      <Box sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <P sx={{ color: "grey.400", mr: 1 }} variant="subtitle1">
            Uploading files to: <strong>{claimantName}</strong>
          </P>
          {!anyFilesUploading && (
            <IconButton onClick={close}>
              <Close />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              border: `1px dashed ${theme.palette.grey[100]}`,
              p: 2,
              alignItems: "start",
              opacity: !anyFilesUploadingOrHaveUploaded ? "" : "0.5",
              backgroundColor: isDraggedOn ? theme.palette.primary.light : undefined,
              borderRadius: `${theme.shape.borderRadius}px`,
            }}
            onDrop={(evt: DragEvent<HTMLDivElement>) => {
              evt.preventDefault();
              evt.stopPropagation();

              if (evt.dataTransfer.files.length > 0) {
                handleFiles([...evt.dataTransfer.files]);
              }
              setIsDraggedOn(false);
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              event.stopPropagation();

              setIsDraggedOn(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              event.stopPropagation();

              setIsDraggedOn(false);
            }}
            onDragEnd={(event) => {
              event.preventDefault();
              event.stopPropagation();

              setIsDraggedOn(false);
            }}
            onDragOver={(e) => e.preventDefault()}>
            <StageNumber step={1} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                pointerEvents: "none",
              }}>
              <Box
                sx={{
                  backgroundColor: "rgb(147 197 253)",
                  height: 40,
                  width: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                }}>
                <Upload />
              </Box>
              <P variant="subtitle1heavy">
                <label
                  style={{
                    marginTop: "8px",
                    fontWeight: "bold",
                    textDecoration: "underline",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    fontFamily: "Inter,arial,sans-serif",
                  }}>
                  Click to select files
                  <input
                    multiple={true}
                    type="file"
                    autoComplete="off"
                    tabIndex={-1}
                    accept="image/*,.pdf,.docx,.txt"
                    style={{ display: "none" }}
                    onChange={onFiles}
                    disabled={!!anyFilesUploadingOrHaveUploaded}
                  />
                </label>
              </P>
              <P>or drag and drop files here</P>
              <P sx={{ mt: 1 }}>Supported File Types:</P>
              <P variant="subtitle2">
                <strong>PDF, PNG, JPG, TIFF, TXT, DOCX</strong>
              </P>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${theme.palette.grey[100]}`,
              p: 2,
              borderRadius: `${theme.shape.borderRadius}px`,
            }}>
            <StageNumber
              step={2}
              color={batchComplete ? (batchHadErrors ? "red" : "green") : "grey"}
              // className="pointer-events-none"
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                flexGrow: 1,
                justifyContent: "center",
              }}>
              {anyFilesUploadingOrHaveUploaded && (
                <FileUploadPrintout
                  successCount={successFiles.length}
                  errorCount={errorFiles.length}
                  filesCount={files.length}
                />
              )}
              {!anyFilesUploadingOrHaveUploaded && (
                <>
                  <P>
                    {isClientErrors
                      ? "Please remove any invalid files before continuing"
                      : "Once you're done adding files, click below to begin uploading."}
                  </P>
                  {!allFilesUploaded && (
                    <P sx={{ mr: 1, mb: 2 }}>
                      <strong>{filesForUpload.length}</strong> {filesForUpload.length === 1 ? "file" : "files"} for
                      upload.
                    </P>
                  )}
                </>
              )}
              {!batchComplete || files.length === 0 || isClientErrors ? (
                <Button
                  variant="contained"
                  disabled={!files.length || anyFilesUploading || isClientErrors}
                  onClick={beginUploads}>
                  Begin Upload
                </Button>
              ) : (
                <Button variant="contained" onClick={close}>
                  Close
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        {files.length === 0 ? (
          <Box
            sx={{
              my: 2,
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              p: 2,
              border: `1px solid ${theme.palette.grey[100]}`,
            }}>
            <P sx={{ color: "grey.400" }}>Files will appear here.</P>
          </Box>
        ) : (
          <FileUploadProgress files={files} onFileDelete={onFileDelete} />
        )}

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }} />
          <Box sx={{ flexGrow: 1 }} />
        </Box>
      </Box>
    </Dialog>
  );
};

export default UploadDialog;
